import { Download, FileCopy } from '@mui/icons-material'
import { Box, Button, CircularProgress, Stack, Tooltip, useMediaQuery, useTheme } from '@mui/material'
import axios from 'axios'
import Holidays from 'date-holidays'
import dayjs from 'dayjs'
import { saveAs } from 'file-saver'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import FileImportDialog from '../../components/Dialog/FileImportDialog'
import CostSidebar from '../../components/Sidebar/cost/CostSidebar'
import InfoSidebar from '../../components/Sidebar/info/InfoSidebar'
import TransferHoursSidebar from '../../components/Sidebar/TransferHoursSidebar'
import WorklogTable from '../../components/Table/Worklog/WorklogTable'
import UserSelector from '../../components/UserSelector/UserSelector'
import defaultProps from '../../config/defaultProps'
import navigation from '../../config/navigation'
import { VACATION_PROJECT_NAME } from '../../store/constants/projectAcronyms'
import { useAppDispatch, useAppSelector, useNotification } from '../../store/redux/hooks'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import { useGetSumQuery } from '../../store/redux/reducers/costSlice'
import {
  getCurrentUser,
  getIsLeaderOrManagement,
  getIsProjectLeader,
} from '../../store/redux/reducers/currentUserSlice'
import { getSelectedDate, monthPickerActions } from '../../store/redux/reducers/monthPickerSlice'
import { pageHeaderActions } from '../../store/redux/reducers/pageHeaderSlice'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { handleGetWorklogExcel } from '../../store/redux/reducers/reportsSliceAxios'
import { getSidebarState } from '../../store/redux/reducers/sidebarSlice'
import { useGetUsersQuery, useGetBlockedUsersWithWorkHoursQuery } from '../../store/redux/reducers/usersSlice'
import { useGetUsersWorklogQuery } from '../../store/redux/reducers/worklogSlice'
import { RequestProps, User } from '../../store/types'
import DateFormat from '../../store/types/enums/DateFormat'
import { sumAllPerMonth, sumProjectPerMonth } from '../../store/utils'
import checkAccessDenied from '../../store/utils/checkAccessDenied'
import { roundUpToNearestMinute } from '../../store/utils/worklogUtils'
import { SquareButton } from '../../styles/ButtonStyles'
import { StyledContainerColumn, StyledContainerRowBetween, StyledContainerRowEnd } from '../../styles/ContainerStyles'
import WorkHoursHistoryView from './WorkHoursHistoryView'

const WorklogView = () => {
  const theme = useTheme()
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'))
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isDownXs = useMediaQuery(theme.breakpoints.down(400))
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'))
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const date = useAppSelector(state => getSelectedDate(state))
  const currentUser = useSelector(getCurrentUser)
  let { id } = useParams()
  const [costValue, setCostValue] = useState<number>(0)
  const [requestProps] = useState<RequestProps>(defaultProps)
  const formattedDate = dayjs(date).format(DateFormat.YearMonth)
  const isDateValid = dayjs(date).isValid()

  const { data: costsResponse, isLoading: isSumLoading } = useGetSumQuery(
    {
      ...requestProps,
      yearMonth: isDateValid ? formattedDate : undefined,
      'author.id': id,
    },
    { skip: !isDateValid }
  )

  useEffect(() => {
    if (costsResponse !== undefined) {
      setCostValue(costsResponse)
    }
  }, [costsResponse])

  const { displayNotification } = useNotification()

  let requestDate = dayjs(date).format(DateFormat.YearMonth)

  if (!date) {
    requestDate = dayjs().format(DateFormat.YearMonth)
  }

  const {
    data: worklog,
    isLoading: isWorklogLoading,
    error,
  } = useGetUsersWorklogQuery({
    employeeId: id,
    date: requestDate,
  })

  useEffect(() => {
    if (error) {
      if (checkAccessDenied(error)) {
        navigate(navigation.unauthorized)
      }
    }
  }, [error, navigate])

  const vacationId = useMemo(() => {
    const vacationProject = worklog
      ?.flatMap(workday => workday.entries)
      .find(workentry => workentry.project.name === VACATION_PROJECT_NAME)
    return vacationProject ? vacationProject.project.id : ''
  }, [worklog])

  const hd = new Holidays('PL', '', '', { types: ['public'] })

  const holidays = hd
    .getHolidays(dayjs(date).year())
    .filter(h => h.type === 'public' && dayjs(h.date).month() === dayjs(date).month())

  const holidayDates = holidays.map(d => dayjs(d.date))
  const businessDays = dayjs(date)
    .businessDaysInMonth()
    .filter(d => holidayDates.find(hd => hd.isSame(d)) === undefined)

  const workHours = businessDays.length * 8
  const vacationHours = sumProjectPerMonth(worklog, vacationId, true)
  const fullVacationHours = sumProjectPerMonth(worklog, vacationId)
  const loggedHours = roundUpToNearestMinute(sumAllPerMonth(worklog) - fullVacationHours)
  const remainingHours = roundUpToNearestMinute(workHours - loggedHours - vacationHours)

  useEffect(() => {
    dispatch(setPageName(t('page.name.worklog')))
    dispatch(setPageIndex(PageIndex.WORKLOG))

    dispatch(
      pageHeaderActions.setButtons({
        monthPicker: true,
      })
    )

    dispatch(monthPickerActions.setDate(dayjs().toString()))
    dispatch(pageHeaderActions.setInfoPopupContent('WORKLOG'))

    return () => {
      dispatch(pageHeaderActions.setInitialState())
    }
  }, [dispatch, t])

  const isLeaderOrManagement = useSelector(getIsLeaderOrManagement)
  const isProjectLeader = useSelector(getIsProjectLeader)

  const { data: selectedUserData } = useGetUsersQuery(
    {
      page: 0,
      size: 1,
      id: id || currentUser?.id,
    },
    { skip: !(isLeaderOrManagement || isProjectLeader) }
  )

  const { data: blockedUsersWithWorkHours } = useGetBlockedUsersWithWorkHoursQuery(
    { yearMonth: formattedDate },
    { skip: !(isLeaderOrManagement || isProjectLeader) || !isDateValid }
  )

  const [selectedUser, setSelectedUser] = useState<User | null>(selectedUserData?.content[0] || currentUser)

  useEffect(() => {
    if (selectedUserData) {
      setSelectedUser(selectedUserData.content[0])
    }
  }, [selectedUserData])

  useEffect(() => {
    if (selectedUser) {
      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(
        breadcrumbsItemsActions.setSelectedItems({
          worklog: `${t('page.name.worklog')} - ${selectedUser.firstName} ${selectedUser.lastName}`,
        })
      )
    }
  }, [dispatch, selectedUser, t])

  useEffect(() => {
    if (
      selectedUser &&
      selectedUser.employeeStatus === 'BLOCKED' &&
      blockedUsersWithWorkHours &&
      !blockedUsersWithWorkHours.find(u => u.id === selectedUser.id)
    ) {
      setSelectedUser(currentUser)
      if (currentUser) {
        navigate(`${navigation.worklog}/${currentUser.id}`)
      }
    }
  }, [selectedUser, blockedUsersWithWorkHours, currentUser, navigate])

  const isReadOnly = currentUser?.id !== id
  const isSidebarOpen = useSelector(getSidebarState)

  const handleDownloadWorklogReport = async () => {
    try {
      if (selectedUser) {
        const response = await handleGetWorklogExcel(selectedUser.id, dayjs(date).year())
        const blob = new Blob([response.data], { type: response.headers['content-type'] as string | undefined })
        const contentDisposition = response.headers['content-disposition']

        if (contentDisposition) {
          let fileName = contentDisposition.split('filename*=')[1]

          if (fileName) {
            fileName = decodeURIComponent(fileName.split("''")[1])
          } else {
            fileName = contentDisposition.split('filename=')[1].replace(/^"(.*)"$/, '$1')
            fileName = decodeURIComponent(fileName)
          }

          saveAs(blob, fileName)
        } else {
          saveAs(blob, 'report')
        }
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const contentType = error.response.headers['content-type'] as string | undefined
        if (contentType && contentType.includes('application/json')) {
          const reader = new FileReader()
          reader.onloadend = () => {
            const jsonData = JSON.parse(reader.result as string)
            if (jsonData.error) {
              let message = ''
              Object.values(jsonData.error).forEach(errorMessage => {
                message += errorMessage + ' '
              })
              displayNotification({ message: message.trim(), type: 'error' })
            } else {
              displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
            }
          }

          reader.readAsText(error.response.data)
        } else {
          displayNotification({ message: t('page.projects.notificationPopup.error'), type: 'error' })
        }
      }
    }
  }

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false)
  const [isEntryChangePresent, setIsEntryChangePresent] = useState<boolean[]>([])

  useEffect(() => {
    if (date) {
      const initialArray = Array(dayjs(date).daysInMonth()).fill(false)
      setIsEntryChangePresent(initialArray)
    }
  }, [date])

  const checkUnsavedChanges = () => {
    return isEntryChangePresent.some(value => value)
  }

  return (
    <StyledContainerColumn
      sx={{
        gap: 2,
        overflow: 'hidden',
        paddingBottom: 3,
        px: { xs: 0, sm: 1, md: 2 },
      }}
    >
      <StyledContainerRowBetween sx={{ py: 1, flexDirection: isDownSm ? 'column' : 'row' }}>
        <Stack
          direction="row"
          gap={isDownSm ? 1 : 4}
          sx={{
            width: {
              xs: '100%',
              sm: isSidebarOpen ? '100%' : '50%',
              md: '50%',
              lg: '40%',
              xl: '30%',
            },
          }}
        >
          {(isLeaderOrManagement || isProjectLeader) && (
            <UserSelector
              selectedUser={selectedUser}
              setSelectedUser={(user: User | null) => {
                setSelectedUser(user)
              }}
              currentUser={currentUser}
              currentView={navigation.worklog}
              employeeStatus="ACTIVE"
              extraUsers={blockedUsersWithWorkHours}
            />
          )}
        </Stack>

        <StyledContainerRowEnd sx={{ maxWidth: isDownMd ? '100%' : '70%', width: isDownMd ? '100%' : '650px' }}>
          {isDownLg && !isDownSm ? (
            <Stack direction="row" gap={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
              <CostSidebar date={date} resize={true} employeeId={id} selectedEmployee={selectedUser} />
              {isDownSm && !isLeaderOrManagement && <InfoSidebar variant="worklog" size="medium" />}
              <Tooltip title={t('page.worklog.export')}>
                <SquareButton color="secondary" size="medium" onClick={handleDownloadWorklogReport}>
                  <Download />
                </SquareButton>
              </Tooltip>
              <FileImportDialog
                squareButton={true}
                selectedUser={selectedUser}
                disabled={currentUser?.id !== selectedUser?.id}
              />
              <TransferHoursSidebar resize={true} employeeId={id} />
              <WorkHoursHistoryView selectedUser={selectedUser} selectedDate={dayjs(date)} />
              <InfoSidebar variant="worklog" size="medium" />
            </Stack>
          ) : (
            <Stack direction="row" gap={2} sx={{ width: '100%' }}>
              <CostSidebar
                date={date}
                resize={(isDownMd || isDownSm) && !isDownXs}
                employeeId={id}
                selectedEmployee={selectedUser}
              />
              {!isDownSm && (
                <>
                  <Button
                    variant={'contained'}
                    startIcon={<FileCopy />}
                    color="secondary"
                    size="medium"
                    onClick={handleDownloadWorklogReport}
                  >
                    {t('page.worklog.export')}
                  </Button>
                  <FileImportDialog selectedUser={selectedUser} disabled={currentUser?.id !== selectedUser?.id} />
                </>
              )}
              <TransferHoursSidebar resize={isDownXs} employeeId={id} fullWidth={isDownSm} />
              <WorkHoursHistoryView selectedUser={selectedUser} selectedDate={dayjs(date)} />
              <InfoSidebar variant="worklog" size="medium" />
            </Stack>
          )}
        </StyledContainerRowEnd>
        {isDownSm && (
          <StyledContainerRowEnd>
            <Stack direction="row" gap={2} sx={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button
                variant={'contained'}
                startIcon={<FileCopy />}
                color="secondary"
                size="medium"
                onClick={handleDownloadWorklogReport}
                fullWidth
              >
                {t('page.worklog.export')}
              </Button>
              <FileImportDialog selectedUser={selectedUser} disabled={currentUser?.id !== selectedUser?.id} fullWidth />
            </Stack>
          </StyledContainerRowEnd>
        )}
      </StyledContainerRowBetween>

      <Box position="relative" width="100%" height="100%" overflow="auto">
        {(isWorklogLoading || isSumLoading) && (
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={2}
          >
            <CircularProgress />
          </Box>
        )}
        <WorklogTable
          worklog={worklog}
          vacationId={vacationId}
          vacationHours={vacationHours}
          workHours={workHours}
          remainingHours={remainingHours}
          loggedHours={loggedHours}
          isReadOnly={isReadOnly}
          cost={costValue}
          isLoading={isWorklogLoading || isSumLoading}
          isLeaderOrManagement={isLeaderOrManagement}
          setIsChangePresent={setIsEntryChangePresent}
        />
      </Box>
      <DiscardChangesDialog
        checkUnsavedChanges={checkUnsavedChanges}
        open={isCancelDialogOpen}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </StyledContainerColumn>
  )
}

export default WorklogView
